/**
 * @format
 */
import { Directive, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import insertTextAtCursor from "insert-text-at-cursor";
import { BsModalService } from "ngx-bootstrap/modal";
import { Subscription } from "rxjs/internal/Subscription";
import { filter } from "rxjs/operators";
import { PreviewEditTemplateActions } from "../../preview-edit-template/state/preview-edit-template.actions";
import { PreviewEditTemplateSelectors } from "../../preview-edit-template/state/preview-edit-template.selectors";
import { PreviewEditTemplateService } from "../../preview-edit-template/state/preview-edit-template.service";
import { BsModalAlertComponent, createAlertModalOptions } from "../modal/modal-alert.component";
import { EditorSelectors } from "../../ng-editor/state/editor.selectors";
import { ContentEditableService, KeyNameAndFunction } from "./contenteditable.service";
import { CustomFocusEvent, CustomInputEvent, CustomKeyboardEvent, CustomPointerEvent } from "./types";
import { TemplateParametersService } from "../../template-parameters/template-parameters.service";

@Directive({
   selector: "[contenteditable]",
})
export class ContenteditableDirective implements OnInit, OnDestroy {
   smsLimit = 480;
   limitPaste = false;
   selectedNode: Element | null;
   cloneTemplate: any;
   stripNewLine = false;
   stripHtml = false;
   isModelUpdated = false;
   isKeyPressed = false;
   smsParsedTextLength = 1;
   subscription = new Subscription();
   isKeyCode: KeyNameAndFunction = {
      ARROW_LEFT: (keyCode: number) => keyCode === 37,
      ARROW_UP: (keyCode: number) => keyCode === 38,
      ARROW_RIGHT: (keyCode: number) => keyCode === 39,
      ARROW_DOWN: (keyCode: number) => keyCode === 40,
   };

   constructor(
      private elementRef: ElementRef,
      private modalService: BsModalService,
      private store: Store<any>,
      private previewEditSelector: PreviewEditTemplateSelectors,
      private previewEditTemplateService: PreviewEditTemplateService,
      private editorSelectors: EditorSelectors,
      private contentEditableService: ContentEditableService,
      private templateParametersService: TemplateParametersService
   ) {
      this.subscription.add(
         this.previewEditSelector
            .selectTemplateUpdateModel()
            .pipe(
               filter((templateUpdateModel) => templateUpdateModel !== undefined),
               filter((templateUpdateModel) => templateUpdateModel.updatedModel !== undefined)
            )
            .subscribe((templateUpdateModel) => {
               this.cloneTemplate = { ...templateUpdateModel.updatedModel };

               return templateUpdateModel;
            })
      );

      this.subscription.add(
         this.previewEditSelector.selectGetSmsParsedTextLength().subscribe((smsParsedTextLength) => {
            this.smsParsedTextLength = smsParsedTextLength;
         })
      );
   }

   @Input("contenteditablemodel") template: any;
   @Input("ng-model") ngModel;
   @Input("disablePasteEvent") disablePasteEvent?: string;
   @HostBinding("attr.contenteditable") enabled;

   @HostListener("click", ["$event"]) onClick(event: CustomPointerEvent<Element>): void {
      this.contentEditableService.saveRange(window.getSelection());
      this.selectedNode = this.contentEditableService.getSelectedNodeFromTarget(event);
      // event.stopPropagation();
   }

   @HostListener("keydown", ["$event"]) onKeydown(event: CustomKeyboardEvent<Element>): void {
      this.isKeyPressed = true;
      this.isModelUpdated = true;
      // enter key
      if (
         event.key === "Enter" &&
         this.contentEditableService.isValid(this.selectedNode) &&
         !this.contentEditableService.isSelectedNodeSMS(this.selectedNode)
      ) {
         event.preventDefault();
         const selection = window.getSelection();
         const currentRange = selection.getRangeAt(0);
         const htmlBrElement = document.createElement("br");
         const parentNode = this.contentEditableService.getParentNode(selection.anchorNode);

         if (!this.contentEditableService.isText_(parentNode) && (<Element>parentNode).id === "PARAMETER") {
            alert("Wrong PARAMETER selection DETECTED");
            return;
         }

         if (this.contentEditableService.isText_(currentRange.startContainer) && parentNode) {
            // case where the selection IS text
            const nodeList = this.contentEditableService.getNodesWithBrInBetween(currentRange, htmlBrElement);

            this.contentEditableService.insertNodesInParentRange(parentNode, nodeList, selection);
            this.contentEditableService.positioningCursor(selection, htmlBrElement);
            this.contentEditableService.dispatchWindowSelectionTimestamp();
            return;
         }

         // case where the selection is NOT text
         if (!this.contentEditableService.isText_(currentRange.startContainer)) {
            const element = <Element>currentRange.startContainer;
            element.appendChild(htmlBrElement);
            this.contentEditableService.positioningCursor(selection, htmlBrElement);
            this.contentEditableService.dispatchWindowSelectionTimestamp();
            return;
         }
      }

      if (
         this.contentEditableService.isValid(this.selectedNode) &&
         this.selectedNode.id.indexOf("sms") !== -1 &&
         event.key !== "Backspace" &&
         this.smsParsedTextLength + 1 > this.smsLimit
      ) {
         if (this.disablePasteEvent === "false") {
            event.preventDefault();
            event.stopPropagation();
         }
      }
   }

   @HostListener("keyup", ["$event"]) onKeyup(event: CustomKeyboardEvent<Element>): void {
      const { keyCode, key } = event;

      if (!this.contentEditableService.isValid(this.selectedNode)) {
         this.selectedNode = this.contentEditableService.getSelectedNodeFromTarget(event);
      }

      if (
         !this.isKeyCode.ARROW_LEFT(keyCode) &&
         !this.isKeyCode.ARROW_RIGHT(keyCode) &&
         !this.isKeyCode.ARROW_UP(keyCode) &&
         !this.isKeyCode.ARROW_DOWN(keyCode) &&
         key !== "Meta"
      ) {
         if (
            this.contentEditableService.isValid(this.selectedNode) &&
            this.selectedNode.id.indexOf("sms") !== -1 &&
            this.smsParsedTextLength + 1 > this.smsLimit
         ) {
            if (this.disablePasteEvent === "false") {
               event.preventDefault();
               event.stopPropagation();
            }
         } else {
            const template = { ...this.cloneTemplate };
            this.isModelUpdated = true;
            if (this.contentEditableService.isValid(this.selectedNode)) {
               template[this.selectedNode.id.split(".")[1]] = this.applyHtmlParsing(this.selectedNode.innerHTML);
               const updateType =
                  this.contentEditableService.isValid(this.selectedNode) &&
                  this.selectedNode.id.indexOf("emailSubject") !== -1
                     ? ""
                     : "model";
               const type = this.selectedNode.id.split(".")[1].startsWith("sms")
                  ? "sms"
                  : this.selectedNode.id.split(".")[1].startsWith("email")
                  ? "email"
                  : "voice";
               this.updateTemplateModel(updateType, template, type);
            }
         }
      }

      this.contentEditableService.saveRange(window.getSelection());
   }

   @HostListener("input", ["$event"]) onInput(event: CustomInputEvent<Element>): void {
      const template = { ...this.cloneTemplate };
      // fix adding parameter
      if (event.data === null && event.target.innerHTML.indexOf("template-context") !== -1) {
         this.isModelUpdated = true;

         if (this.contentEditableService.isValid(this.selectedNode)) {
            template[this.selectedNode.id.split(".")[1]] = this.selectedNode.innerHTML;
            this.updateTemplateModel("model", template, "");
         }
      }

      if (
         this.contentEditableService.isValid(this.selectedNode) &&
         this.selectedNode.id.indexOf("sms") !== -1 &&
         event.data !== undefined &&
         event.data !== null &&
         event.data === " " &&
         this.smsParsedTextLength + event.data.length > this.smsLimit
      ) {
         event.preventDefault();
         event.stopPropagation();
      }
   }

   @HostListener("blur", ["$event"]) onBlur(event: CustomFocusEvent<Element>): void {
      try {
         if (this.contentEditableService.isValid(event.target)) {
            this.selectedNode = event.target;
         }

         // console.log('------contenteditable blur1 ----- ', this.selectedNode, ' ------- ', this.isModelUpdated);
         if (this.isModelUpdated && this.contentEditableService.isValid(this.selectedNode)) {
            const template = { ...this.cloneTemplate };
            template[this.selectedNode.id.split(".")[1]] =
               event.undoRedo !== undefined
                  ? this.applyHtmlParsing(document.getElementById(this.selectedNode.id).innerHTML)
                  : this.applyHtmlParsing(this.selectedNode.innerHTML);
            // If we set it as model for emailSubject, every key press and blur will reload
            const updateType = "model";
            const type = this.selectedNode.id.split(".")[1].startsWith("sms")
               ? "sms"
               : this.selectedNode.id.split(".")[1].startsWith("email")
               ? "email"
               : "voice";
            this.updateTemplateModel(updateType, template, type);
            this.isModelUpdated = false;
         }
         console.log("blur", event);
         if (event.undoRedo === undefined) {
            event.stopPropagation();
         }
      } catch (error) {
         console.error("(Custom Error)ContentEditable OnBlur: ", error);
      }
   }

   @HostListener("paste", ["$event"]) onPaste(event: ClipboardEvent): void {
      if (this.disablePasteEvent === "false") {
         const clipboardData = event.clipboardData;
         let pastedText = clipboardData.getData("text");
         if (this.limitPaste) {
            if (this.smsLimitReached(pastedText)) {
               // console.log('Pasting disabled!');
               this.modalService.show(
                  BsModalAlertComponent,
                  createAlertModalOptions(
                     "Paste Rejected",
                     "This will exceed the 480 character max. Please shorten text first."
                  )
               );
               event.preventDefault();
               event.stopPropagation();
            }
         } else {
            event.preventDefault();
            event.stopPropagation();
            insertTextAtCursor(this.selectedNode, pastedText);
         }
      }
   }

   ngOnInit(): void {
      this.selectedNode = null;
      let html = this.elementRef.nativeElement.innerHTML;

      if (html.indexOf("comment") !== -1) {
         html = html.toString().replaceAll("comment", "template-context");
      }

      html = html.replaceAll(/<div><br><\/div>/g, "<br/>").replaceAll(/<div><br\/><\/div>/g, "<br/>");

      if (
         this.elementRef.nativeElement.attributes["strip-nbsp"] !== undefined &&
         this.elementRef.nativeElement.attributes["strip-nbsp"].nodeValue === true
      ) {
         html = html.replaceAll(/\&nbsp[;]?/g, " ");
      }

      if (this.elementRef.nativeElement.attributes["strip-newLine"] !== undefined) {
         this.stripNewLine = this.elementRef.nativeElement.attributes["strip-newLine"].nodeValue;
      }

      if (this.elementRef.nativeElement.attributes["limit-paste"] !== undefined) {
         this.limitPaste = this.elementRef.nativeElement.attributes["limit-paste"].nodeValue;
      }

      if (this.elementRef.nativeElement.attributes["contenteditable"] !== undefined) {
         this.enabled = this.elementRef.nativeElement.attributes["contenteditable"].nodeValue;
      }

      if (this.elementRef.nativeElement.attributes["strip-html"] !== undefined) {
         this.stripHtml = this.elementRef.nativeElement.attributes["strip-html"].nodeValue;
      }

      this.elementRef.nativeElement.innerHTML = html;
   }

   ngOnDestroy(): void {
      this.subscription.unsubscribe();
   }

   smsLimitReached(addString): boolean {
      return this.smsParsedTextLength + addString.length > this.smsLimit;
   }

   escapeXmlChars(html): string {
      let escapeHtml = html;
      if (escapeHtml !== undefined && typeof escapeHtml === "string") {
         while (escapeHtml.indexOf("&lt;") !== -1 && escapeHtml.indexOf("&gt;") !== -1) {
            escapeHtml = escapeHtml.replaceAll(/&lt;/g, "<").replaceAll(/&gt;/g, ">");
         }
      }

      return escapeHtml;
   }

   applyHtmlParsing(html): string {
      let trimHtml = html;

      if (trimHtml !== null) {
         // this is required when the model contains only number (example 280)
         if (typeof trimHtml !== "string") {
            trimHtml = trimHtml.toString();
         }

         if (window.navigator.userAgent.indexOf("safari") === -1 && trimHtml.innerHTML === "<br>") {
            trimHtml = "";
         }

         if (trimHtml !== undefined && trimHtml.indexOf("&lt;") !== -1 && trimHtml.indexOf("&gt;") !== -1) {
            trimHtml = this.escapeXmlChars(trimHtml);
         }

         if (this.stripNewLine && trimHtml !== undefined) {
            trimHtml = this.stripNewLineForModel(trimHtml);
         }

         if (trimHtml !== undefined && trimHtml.indexOf("&nbsp;") !== -1) {
            // trimHtml = trimHtml.replaceAll('&nbsp;', ' ');
         }

         trimHtml = this.contentEditableService.removeSpanFromTemplateContext(trimHtml);

         if (this.stripHtml && trimHtml !== undefined) {
            trimHtml = trimHtml.replaceAll(/(<span)([^])*?(>)/g, "").replaceAll("</span>", "");
         }
      }

      return trimHtml;
   }

   stripNewLineForModel(html): string {
      let trimHtml = html;
      trimHtml = trimHtml.replaceAll(/(<div[^>]*)([^<]*|\n)*?(<br[^>]*>)([^<]*|\n)*?(<\/div>)/g, " "); // remove enter with blank space

      return trimHtml;
   }

   templateContextFormat(tData, type): any {
      const updatedValue = { isModelUpdated: false, value: tData };
      let value: any = tData;
      try {
         if (
            (value.indexOf("<comment") !== -1 ||
               value.indexOf("comment>") !== -1 ||
               value.indexOf("<context") !== -1) &&
            type === "db"
         ) {
            updatedValue.isModelUpdated = true;
         }

         let matches = value.match(/(<(img)(>|\s)[^<]+?\/>)/g); // check if img tag is self closed
         if (value.indexOf("<img") !== -1) {
            if (matches !== null) {
               value = value.replace(/(<(img)(>|\s)[^<\/]+?>)/g, "imagetag");
            }
         }

         value = value
            .replaceAll("<comment", "<template-context")
            .replaceAll("comment>", "template-context>")
            .replaceAll("<context", "<template-context")
            .replaceAll("</context>", "</template-context")
            .replaceAll("<br/>", "<br>")
            .replaceAll("/>", "></template-context>")
            .replaceAll("<br>", "<br/>");

         if (matches !== null) {
            let i = 0;
            let newHtml = value;
            while (i !== matches.length) {
               newHtml = newHtml.replace("imagetag", matches[i].replace("/>", ">") + "</img>");
               i++;
            }
            value = newHtml;
         }

         // not needed when we get template from db or file
         // value = this.contentEditableService.removeSpanFromTemplateContext(value);
      } catch (e) {
         // console.log(value);
      }
      updatedValue.value = value;

      return updatedValue;
   }

   addParameter(selection, templateParameters, selectedTemplateParameter, selectedNode): void {
      this.selectedNode = selectedNode;
      //const data = templateParameters.find((parameter) => parameter.value === selectedTemplateParameter);

      if (this.selectedNode.id.split(".")[0] === "template") {
         // restoring cursor position
         this.contentEditableService.restoreRange();
         const restoredRange = this.contentEditableService.getRange();

         const template = { ...this.cloneTemplate };
         const templateVariableName = this.contentEditableService.getTemplateVariableName(this.selectedNode);

         // creating auxiliary element to insert parameter
         const auxiliaryElement = document.createElement("SPAN");

         if (this.contentEditableService.isEmailMessageParameter(templateVariableName)) {
            auxiliaryElement.innerHTML = this.templateParametersService.getElementWrapped(selectedTemplateParameter);
         } else {
            auxiliaryElement.innerHTML = selectedTemplateParameter;
         }

         //inserting parameter on right position
         const parameterElement = auxiliaryElement.firstElementChild;
         restoredRange.insertNode(parameterElement);

         this.contentEditableService.positioningCursor(window.getSelection(), parameterElement);
         this.contentEditableService.dispatchWindowSelectionTimestamp();

         const selectedInnerHtml = this.contentEditableService.getInnerHtmlFormElement(this.selectedNode);
         template[templateVariableName] = this.contentEditableService.removeSpanFromTemplateContext(selectedInnerHtml);

         // If we set it as model for emailSubject, every key press and blur will reload
         const updateType = "model";
         this.updateTemplateModel(updateType, template, "");
         this.isModelUpdated = false;
      }
   }

   updateTemplateModel(updateType, template, type): any {
      template = this.contentEditableService.cleanTemplateModelVariable("voiceMessage", template);
      template = this.contentEditableService.cleanTemplateModelVariable("smsMessage", template);

      const templateUpdateModel = {
         updatedModel: template,
         isModelUpdated: true,
         updateType,
         type,
      };
      this.store.dispatch(PreviewEditTemplateActions.templateUpdateModel({ templateUpdateModel }));
   }
}
